/**
 * @vortex-os/ontos — LLM enrichment types (0.2.0, agent-mediated).
 *
 * The ontos library has no generative LLM of its own (memory-extended ships only
 * a local embedder). So enrichment is AGENT-MEDIATED: ontos emits an extraction
 * REQUEST, the running agent (Claude Code) fulfils it and returns RAW proposals
 * (already self/cross-checked), and the library validates → tiers → stores them as
 * PROPOSALS — never into the trusted `relations` graph. See the 0.2.0 design.
 */

import type { ObjectType, RelationType } from "../types.js";
import type { ExtractResult, RecordInput } from "../extract/types.js";
import type { ConversationUse, EvidenceKind, FactType } from "./trust-policy.js";

export type { ConversationUse, EvidenceKind, FactType } from "./trust-policy.js";

/**
 * The trust/usage policy fields (2026-06-20 pivot). All OPTIONAL and additive: a
 * proposal without them is the pre-pivot shape, and the store derives sane defaults
 * (evidenceKind from verifiedBy, score from evidenceKind, conversationUse/guardEligible
 * derived). See `trust-policy.ts` for the rules and the decision-log for the why.
 */
export interface TrustPolicyFields {
  /** How the proposal earned trust (superset of `verifiedBy`). */
  readonly evidenceKind?: EvidenceKind;
  /** Dialogue confidence hedge (low/medium/high) — derived & maintained by the store. */
  readonly conversationUse?: ConversationUse;
  /** May a RISK guard read it? Derived; true only for deterministic/explicit. */
  readonly guardEligible?: boolean;
  /** Sticky: a conversation-sourced write ever touched it → never auto guard-eligible. */
  readonly conversationTouched?: boolean;
  /** A user correction stands against it (acquiescence reset, use forced low). */
  readonly contested?: boolean;
  /** Accumulating trust score (verification seeds it, acquiescence nudges it). */
  readonly score?: number;
  /** Count of distinct-session acquiescences accepted (reset by a correction). */
  readonly acquiescenceCount?: number;
  /** ISO date of the most recent accepted acquiescence (for decay + dedup). */
  readonly lastAcquiescedAt?: string | null;
  /** What kind of fact — gates the objective cap and time decay. */
  readonly factType?: FactType | null;
  /** Parent proposal id this was derived from (derived ≤ parent clamp). */
  readonly derivedFrom?: string | null;
}

/** Trust tier of a stored proposal (surfacing rank, NOT graph membership). */
export type Trust = "high" | "medium" | "low";

/** How a proposal earned its trust. */
export type VerifiedBy = "deterministic" | "cross-model" | "self";

/**
 * Phase-2: where a proposal's evidence came from. `record` = a curated markdown
 * record (Phase-1, the default). `conversation` = a raw session transcript, which
 * is noisier → its trust is CAPPED at MEDIUM (never auto-HIGH), since a "matching"
 * deterministic record may itself be a summary derived FROM that very conversation
 * (circular corroboration — Codex review 2026-06-19 #3). HIGH stays human-only.
 */
export type SourceKind = "record" | "conversation";

export const TRUST_RANK: Readonly<Record<Trust, number>> = { high: 3, medium: 2, low: 1 };

/** Domain/range constraint for a relation type (object types, by id prefix). */
export interface DomainRange {
  /** Allowed source object types; empty = any. */
  readonly domain: readonly ObjectType[];
  /** Allowed target object types; empty = any. */
  readonly range: readonly ObjectType[];
  /** Symmetric relations (contradicts): both ends must be the same type set. */
  readonly symmetric?: boolean;
}

/**
 * What ontos hands the agent for one record: the prose to read, what deterministic
 * extraction already found (so the agent fills GAPS, not duplicates), the controlled
 * vocabulary + domain/range, and the output contract. Pure data — no I/O.
 */
export interface EnrichmentRequest {
  readonly path: string;
  readonly category: string;
  readonly prose: string;
  /** Already-known objects/relations from deterministic extraction (don't re-propose). */
  readonly alreadyFound: { readonly objectIds: readonly string[]; readonly relations: readonly string[] };
  readonly vocabulary: {
    readonly objectTypes: readonly ObjectType[];
    readonly relationTypes: readonly RelationType[];
    readonly domainRange: Readonly<Record<RelationType, DomainRange>>;
  };
  readonly rules: readonly string[];
}

/**
 * One relation the agent proposes, with its own cross/self verification verdict.
 * The agent does extract + check and submits the final verdict per proposal.
 */
export interface RawProposal {
  readonly sourceRef: string;
  readonly rel: string;
  readonly targetRef: string;
  readonly evidencePath: string;
  readonly evidenceSpan?: string | null;
  readonly confidence: number;
  /** Which checker the agent used for this proposal. */
  readonly verifier: "cross-model" | "self";
  /** Did the checker agree the relation is real? */
  readonly agreed: boolean;
  /**
   * The EXTRACTOR's own sensitivity judgment (cross-vendor egress gate, layer 3). True
   * = private/confidential (health, relationships, named third parties, business
   * secrets) → never sent to a second vendor. This is ADVISORY on top of the
   * deterministic floor (`classifySensitivity`); the gate blocks if EITHER flags.
   */
  readonly sensitive?: boolean;
}

/** A proposal that passed validation (vocab + domain/range + id resolution). */
export interface ValidatedProposal {
  readonly relId: string;
  readonly rel: RelationType;
  readonly sourceId: string;
  readonly targetId: string;
  readonly sourceResolved: boolean;
  readonly targetResolved: boolean;
  readonly confidence: number;
  readonly evidencePath: string;
  readonly evidenceSpan: string | null;
}

/** A fully tiered proposal, ready to store in `relation_proposals`. */
export interface RelationProposal extends ValidatedProposal, TrustPolicyFields {
  readonly trust: Trust;
  readonly trustRank: number;
  readonly verifiedBy: VerifiedBy;
  readonly sourceRecordHash?: string | null;
  readonly proposedAt?: string | null;
  /** ISO date promoted into the trusted graph; null = still a proposal. */
  readonly confirmed?: string | null;
  /** ISO date a human rejected it (sticky — not re-surfaced or re-asked). */
  readonly rejectedAt?: string | null;
  /** ISO date last surfaced as an in-context question (cooldown for re-asking). */
  readonly askedAt?: string | null;
  /** Phase-2 provenance. Defaults to "record" (Phase-1 behaviour). */
  readonly sourceKind?: SourceKind;
  /** Originating transcript/record id — circular-corroboration guard (Phase-2). */
  readonly sourceFamily?: string | null;
  /** Batch id, for run-level rollback of a bad extractor run (Phase-2). */
  readonly extractionRunId?: string | null;
}

/**
 * Phase-2: one append-only evidence row anchoring a proposal to verifiable source
 * text. Cross-conversation corroboration = COUNT(DISTINCT transcriptId) over these
 * (a derived value, never a stored counter — idempotent under re-runs). The
 * (relId, transcriptId, messageId, quoteHash) tuple is the dedup key.
 */
export interface ConversationEvidence {
  readonly relId: string;
  readonly transcriptId: string;
  readonly messageId: string;
  readonly spanStart?: number | null;
  readonly spanEnd?: number | null;
  /** Hash of the cited quote, validated against the transcript content (anti-forgery). */
  readonly quoteHash: string;
  /** user | agent | third-party — speaker authority weighting (§7). */
  readonly speaker?: string | null;
  readonly runId?: string | null;
  /** When the cited MESSAGE was said — the fact's date anchor. Taken from the
   *  parsed transcript at ingest (authoritative), never from the LLM output. */
  readonly messageAt?: string | null;
}

/** Phase-2 chunk-level processing outcome (transcript-level stamping is unsafe — #9). */
export type ChunkStatus =
  | "processed_no_candidates"
  | "processed_with_candidates"
  | "failed_retryable"
  | "failed_permanent";

export interface ChunkState {
  readonly transcriptId: string;
  readonly chunkId: string;
  readonly status: ChunkStatus;
  readonly extractorVersion?: string | null;
  readonly promptVersion?: string | null;
  readonly schemaVersion?: string | null;
  readonly processedAt?: string | null;
}

/**
 * Phase-2 (MVP-B): one NEW object the agent proposes from a conversation (an entity
 * not yet in the trusted `objects` set). Anchored like a relation proposal.
 */
export interface RawObjectProposal {
  readonly type: string; // must be an ObjectType
  readonly label: string;
  readonly proposedId?: string; // optional explicit id; else derived from type + label
  readonly confidence: number;
  readonly verifier: "cross-model" | "self";
  readonly agreed: boolean;
  readonly messageId: string;
  readonly quote: string;
  readonly speaker?: "user" | "agent";
  /** Extractor's sensitivity judgment — cross-vendor egress gate layer 3 (see RawProposal). */
  readonly sensitive?: boolean;
}

/** A validated + tiered object proposal, ready to store in `object_proposals`. */
export interface ObjectProposal extends TrustPolicyFields {
  readonly objId: string;
  readonly type: import("../types.js").ObjectType;
  readonly label: string;
  readonly trust: Trust;
  readonly trustRank: number;
  readonly verifiedBy: VerifiedBy;
  readonly confidence: number;
  readonly sourceKind?: SourceKind;
  readonly sourceFamily?: string | null;
  readonly evidencePath?: string | null;
  readonly evidenceSpan?: string | null;
  readonly extractionRunId?: string | null;
  readonly proposedAt?: string | null;
  readonly confirmed?: string | null;
  readonly rejectedAt?: string | null;
  readonly askedAt?: string | null;
}

/** Why a raw proposal was dropped (collected into the run report; never stored). */
export type RejectReason =
  | "unknown-relation"
  | "domain-range"
  | "no-evidence"
  | "bad-confidence"
  | "bad-verdict"
  | "verifier-disagreed"
  /** Phase-2: the cited quote does not occur in the named transcript message (forged/paraphrased anchor). */
  | "bad-anchor"
  /** Phase-2 (MVP-B) object proposals. */
  | "unknown-type"
  | "bad-label";

export interface IngestReport {
  readonly accepted: number;
  readonly rejected: readonly { readonly reason: RejectReason; readonly raw: RawProposal }[];
}

export type { ExtractResult, RecordInput };
